package com.evoapps.evoshift.domain.usecase.user

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.availability.AvailabilitySlotMapping
import com.evoapps.evoshift.domain.model.AvailabilityDay
import com.evoapps.evoshift.domain.model.AvailabilitySharing
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.SharedAvailability
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.usecase.availability.AvailabilityDistribution
import com.evoapps.evoshift.domain.usecase.availability.DistributionOutcome

/**
 * SH-135: saves the person's month once for all their workplaces
 * (`users/{uid}/availability/{YYYY-MM}`) and gives each workplace it goes to a copy on its own
 * slots: the workplace the form was filled at, the same owner's other workplaces, and other
 * owners' workplaces when the person shares with all employers ([AvailabilityDistribution]).
 * A campaign at any of those workplaces is answered by the same month.
 */
class SubmitAvailabilityUseCase(
    private val availabilityRepository: AvailabilityRepository,
    private val distribution: AvailabilityDistribution
) {
    /** The month as hours ([days]: day of month to the day), filled in at [workplaceId]. */
    suspend fun submit(
        userId: String,
        workplaceId: String,
        month: String, // "2026-02"
        days: Map<Int, AvailabilityDay>,
        isFilled: Boolean
    ): Result<DistributionOutcome> = runCatching {
        // A month the workplace's managers locked is no longer the person's to change there.
        if (availabilityRepository.findAvailability(userId, workplaceId, month).getOrNull()?.isLocked == true) {
            throw IllegalStateException("Availability for this month is locked")
        }
        val workplaces = distribution.workplacesOf(userId)
        val here = workplaces.firstOrNull { it.id == workplaceId }
            ?: throw IllegalStateException("Not working at this workplace")
        val sharing = availabilityRepository.getSharing(userId).getOrDefault(AvailabilitySharing())
        // Before the first answer after SH-135, the employers that already had the month keep it.
        val previous = availabilityRepository.getSharedAvailability(userId, month).getOrNull()
            ?: distribution.fromLegacy(userId, month, workplaces)
        val shared = SharedAvailability(
            userId = userId,
            month = month,
            days = days,
            // Saving a draft after sending does not take the month back from anyone.
            isFilled = isFilled || previous?.isFilled == true,
            releasedToOwnerIds = previous?.releasedToOwnerIds.orEmpty() + here.ownerId,
            updatedAt = AppClock.now().toString()
        )
        availabilityRepository.saveSharedAvailability(shared).getOrThrow()
        val outcome = distribution.distribute(shared, workplaces, sharing)
        if (workplaceId in outcome.failed) throw IllegalStateException("Availability could not be saved")
        outcome
    }

    /**
     * The form of one workplace before SH-135: [days] on that workplace's slots (comments stay
     * with it). [rosterId] is kept for the callers; the copies are keyed by workplace and month.
     */
    @Suppress("UNUSED_PARAMETER")
    suspend operator fun invoke(
        userId: String,
        rosterId: String,
        workplaceId: String,
        month: String,
        days: Map<Int, DayPreference>,
        isFilled: Boolean
    ): Result<Unit> = runCatching {
        val slots = distribution.workplacesOf(userId).firstOrNull { it.id == workplaceId }?.settings?.definedAvailabilitySlots.orEmpty()
        val asHours = days.mapValues { (_, pref) -> AvailabilitySlotMapping.fromWorkplaceDay(pref, slots, workplaceId) }
        submit(userId, workplaceId, month, asHours, isFilled).getOrThrow()
        Unit
    }
}
